import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Plus, AlertTriangle } from 'lucide-react';

const API = 'https://crm.ashbournesystems.ca/api';

export default function Invoices({ token }) {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState('all');
  const [form, setForm] = useState({ client_id: '', invoice_number: '', amount: '', type: 'project', due_date: '', notes: '' });
  const headers = { Authorization: `Bearer ${token}` };

  const load = () => {
    axios.get(`${API}/invoices`, { headers }).then(r => setInvoices(r.data)).catch(() => {});
    axios.get(`${API}/clients`, { headers }).then(r => setClients(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/invoices`, form, { headers });
      toast.success('Invoice created');
      setShowForm(false);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const markPaid = async (id) => {
    try {
      await axios.put(`${API}/invoices/${id}/paid`, { amount_paid: invoices.find(i => i.id === id)?.amount, payment_method: 'LemonSqueezy' }, { headers });
      toast.success('Invoice marked as paid');
      load();
    } catch { toast.error('Error'); }
  };

  const filtered = filter === 'all' ? invoices : invoices.filter(i => i.status === filter);
  const totalOutstanding = invoices.filter(i => i.status !== 'paid').reduce((s, i) => s + parseFloat(i.amount || 0), 0);
  const overdue = invoices.filter(i => i.status !== 'paid' && i.due_date && new Date(i.due_date) < new Date());

  const inputStyle = { width: '100%', padding: '0.65rem', background: '#0e0804', border: '1px solid #2a1a0e', color: '#F5ECD7', fontSize: '0.82rem', outline: 'none', boxSizing: 'border-box', marginBottom: '0.75rem' };
  const labelStyle = { display: 'block', fontSize: '0.62rem', color: '#8a6840', letterSpacing: '0.1em', marginBottom: '4px' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#F5ECD7', marginBottom: '4px' }}>Invoices</h1>
          <p style={{ color: '#8a6840', fontSize: '0.85rem' }}>${totalOutstanding.toFixed(2)} outstanding</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#B85A22', border: 'none', color: '#F5ECD7', padding: '0.75rem 1.25rem', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 'bold' }}>
          <Plus size={16} /> New Invoice
        </button>
      </div>

      {overdue.length > 0 && (
        <div style={{ background: '#1a0808', border: '1px solid #8b2020', borderLeft: '4px solid #8b2020', padding: '1rem 1.5rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <AlertTriangle size={18} color="#8b2020" />
          <div style={{ color: '#F5ECD7', fontSize: '0.82rem' }}>{overdue.length} overdue invoice{overdue.length > 1 ? 's' : ''} — send late payment notices</div>
        </div>
      )}

      {showForm && (
        <div style={{ background: '#1a0f08', border: '1px solid #2a1a0e', borderTop: '3px solid #B85A22', padding: '1.5rem', marginBottom: '2rem' }}>
          <h3 style={{ color: '#F5ECD7', marginBottom: '1.5rem', fontSize: '1rem' }}>New Invoice</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div><label style={labelStyle}>CLIENT *</label>
                <select style={inputStyle} value={form.client_id} onChange={e => setForm({...form, client_id: e.target.value})} required>
                  <option value="">Select client...</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.business_name || c.name}</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>INVOICE # *</label><input style={inputStyle} value={form.invoice_number} onChange={e => setForm({...form, invoice_number: e.target.value})} placeholder="AD-2025-001" required /></div>
              <div><label style={labelStyle}>AMOUNT (CAD) *</label><input style={inputStyle} type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} required /></div>
              <div><label style={labelStyle}>DUE DATE</label><input style={inputStyle} type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} /></div>
              <div><label style={labelStyle}>TYPE</label>
                <select style={inputStyle} value={form.type} onChange={e => setForm({...form, type: e.target.value})}>
                  <option value="project">Project</option>
                  <option value="deposit">Deposit</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="change_order">Change Order</option>
                </select>
              </div>
              <div><label style={labelStyle}>NOTES</label><input style={inputStyle} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
            </div>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button type="submit" style={{ background: '#B85A22', border: 'none', color: '#F5ECD7', padding: '0.75rem 1.5rem', cursor: 'pointer', fontWeight: 'bold' }}>Create Invoice</button>
              <button type="button" onClick={() => setShowForm(false)} style={{ background: 'transparent', border: '1px solid #2a1a0e', color: '#8a6840', padding: '0.75rem 1.5rem', cursor: 'pointer' }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
        {['all', 'unpaid', 'paid', 'overdue'].map(s => (
          <button key={s} onClick={() => setFilter(s)} style={{ padding: '0.4rem 1rem', background: filter === s ? '#B85A22' : 'transparent', border: `1px solid ${filter === s ? '#B85A22' : '#2a1a0e'}`, color: filter === s ? '#F5ECD7' : '#8a6840', cursor: 'pointer', fontSize: '0.72rem', textTransform: 'capitalize' }}>
            {s}
          </button>
        ))}
      </div>

      <div style={{ background: '#1a0f08', border: '1px solid #2a1a0e' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#8a6840' }}>No invoices found</div>
        ) : filtered.map((inv, i) => {
          const isOverdue = inv.status !== 'paid' && inv.due_date && new Date(inv.due_date) < new Date();
          return (
            <div key={inv.id} style={{ padding: '1rem 1.5rem', borderBottom: i < filtered.length - 1 ? '1px solid #2a1a0e' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: isOverdue ? 'rgba(139,32,32,0.05)' : 'transparent' }}>
              <div>
                <div style={{ color: '#F5ECD7', fontWeight: 'bold', fontSize: '0.9rem' }}>{inv.invoice_number}</div>
                <div style={{ color: '#8a6840', fontSize: '0.75rem' }}>{inv.business_name || inv.client_name} · {inv.type}</div>
                <div style={{ color: '#8a6840', fontSize: '0.72rem' }}>Due: {inv.due_date ? new Date(inv.due_date).toLocaleDateString() : 'N/A'}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: '#C8941A', fontWeight: 'bold', fontSize: '1rem' }}>${inv.amount}</div>
                  <span style={{ fontSize: '0.6rem', color: inv.status === 'paid' ? '#4a9a4a' : isOverdue ? '#8b2020' : '#C8941A' }}>{isOverdue && inv.status !== 'paid' ? 'OVERDUE' : inv.status?.toUpperCase()}</span>
                </div>
                {inv.status !== 'paid' && (
                  <button onClick={() => markPaid(inv.id)} style={{ background: 'transparent', border: '1px solid #2d6a2d', color: '#4a9a4a', padding: '0.4rem 0.75rem', cursor: 'pointer', fontSize: '0.7rem' }}>
                    Mark Paid
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}